import { useTranslation } from 'react-i18next';
import { Card, CardHeader, CardTitle } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Users, Plus } from 'lucide-react';
import { cn } from '@/shared/lib/utils';
import { UserSelectorAvatar } from '@/shared/components/user-selector/UserSelectorAvatar';
import { formatDistanceToNow } from 'date-fns';
import { enUS, he } from 'date-fns/locale';
import type {
  ManagerEmployeeWithMeetingsResponse,
  EmployeeInfo,
  OneOnOne,
} from '@momentum/types';
import type { DisplayEmployeeWithRole } from '../shared/types/one-on-one.types';
import { ProfessionalMeetingsAsEmployee } from './ProfessionalMeetingsAsEmployee';
import { ProfessionalMeetingsAsFacilitator } from './ProfessionalMeetingsAsFacilitator';
import { MeetingCard } from './MeetingCard';

interface ProfessionalMeetingWithEmployee {
  employeeId: string;
  employeeName: string;
  employeeImageUrl?: string;
  employeeRole: string;
  meetings: OneOnOne[];
}

interface EmployeeListSidebarProps {
  managedEmployees: ManagerEmployeeWithMeetingsResponse[] | undefined;
  displayEmployee: DisplayEmployeeWithRole | null;
  onSelectEmployee: (employee: DisplayEmployeeWithRole) => void;
  manager?: EmployeeInfo | null;
  managerMeetings?: OneOnOne[];
  onCreateMeetingWithManager?: () => void;
  isCreatingWithManager?: boolean;
  professionalMeetingsAsManager?: ProfessionalMeetingWithEmployee[];
  allEmployees: EmployeeInfo[];
  onCreateMeetingWithCoworker?: () => void;
}

const getLastMeetingText = (
  lastMeetingDate: string | null | undefined,
  neverText: string,
  locale: 'en' | 'he'
) => {
  if (!lastMeetingDate) return neverText;
  const locales = {
    en: enUS,
    he: he,
  };
  return formatDistanceToNow(new Date(lastMeetingDate), {
    addSuffix: true,
    locale: locales[locale] || enUS,
  });
};

export const EmployeeListSidebar = ({
  managedEmployees,
  displayEmployee,
  onSelectEmployee,
  manager,
  managerMeetings,
  onCreateMeetingWithManager,
  isCreatingWithManager = false,
  professionalMeetingsAsManager,
  allEmployees,
  onCreateMeetingWithCoworker,
}: EmployeeListSidebarProps) => {
  const { t, i18n } = useTranslation('oneOnOne');

  const isManagerSelected = displayEmployee?._id === manager?._id;

  const meetingsWithDates =
    managerMeetings?.filter((meeting) => meeting.schedualedDate) || [];

  const managerLastMeetingDate =
    meetingsWithDates.length > 0
      ? meetingsWithDates.reduce((latest, meeting) => {
          if (!meeting.schedualedDate) return latest;
          const meetingDate = new Date(meeting.schedualedDate);
          const latestDate = latest ? new Date(latest) : new Date(0);
          return meetingDate > latestDate ? meeting.schedualedDate : latest;
        }, null as string | null)
      : null;

  const managerLastMeetingText = getLastMeetingText(
    managerLastMeetingDate,
    t('never'),
    i18n.language as 'en' | 'he'
  );

  return (
    <Card className="flex flex-1 flex-col bg-white rounded-xl shadow-md border border-gray-200">
      {/* Main Title */}
      <CardHeader className="pb-4">
        <CardTitle className="text-lg font-semibold text-slate-900">
          {t('your1on1Meetings')}
        </CardTitle>
      </CardHeader>
      <div className="flex justify-center">
        <div className="w-[90%] border-t border-slate-200" />
      </div>

      {/* Manager Section */}
      <div className="px-6 pt-6 pb-2">
        <h3 className="mb-4 text-xs font-medium uppercase tracking-wide text-slate-500">
          {t('myManager')}
        </h3>
        {manager ? (
          <div className="space-y-4">
            <Button
              variant="ghost"
              onClick={() => {
                if (manager) {
                  onSelectEmployee({
                    _id: manager._id,
                    firstName: manager.personalInfo.firstName,
                    lastName: manager.personalInfo.lastName,
                    imageUrl: manager.personalInfo.imageUrl,
                    role: manager.role,
                    managerId: manager.workInfo.managerId,
                    lastMeetingDate: managerLastMeetingDate || undefined,
                    daysSinceLastMeeting: undefined,
                    allMeetings: managerMeetings || [],
                    meetingRole: 'employee',
                  });
                }
              }}
              className={cn(
                'h-auto w-full justify-start rounded-lg border p-3 transition-all',
                isManagerSelected
                  ? 'border-primary bg-primary/5 shadow-sm hover:bg-primary/10'
                  : 'border-slate-200 bg-slate-50 hover:border-slate-300 hover:bg-slate-100'
              )}
            >
              <div className="flex w-full items-center gap-3">
                <UserSelectorAvatar
                  fullName={`${manager.personalInfo.firstName} ${manager.personalInfo.lastName}`}
                  imageUrl={manager.personalInfo.imageUrl}
                  size="md"
                  shape="square"
                />
                <div className="flex-1 min-w-0 text-start">
                  <p className="text-sm font-medium text-slate-900 truncate">
                    {manager.personalInfo.firstName}{' '}
                    {manager.personalInfo.lastName}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t('lastMeeting')}: {managerLastMeetingText}
                  </p>
                </div>
              </div>
            </Button>
            {onCreateMeetingWithManager && (
              <Button
                onClick={onCreateMeetingWithManager}
                disabled={isCreatingWithManager}
                className="w-full h-10 gap-2"
              >
                {isCreatingWithManager ? (
                  <>
                    <div className="h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    {t('creating')}
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4" />
                    {t('create1on1WithManager')}
                  </>
                )}
              </Button>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-3 rounded-lg bg-slate-50 p-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-slate-200">
              <Users className="h-5 w-5 text-slate-400" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-slate-700">
                {t('noManagerAssigned')}
              </p>
              <p className="text-xs text-muted-foreground">
                {t('noManagerDescription')}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Professional Meetings (as Employee) Section */}
      <ProfessionalMeetingsAsEmployee
        displayEmployee={displayEmployee}
        onSelectEmployee={onSelectEmployee}
        allEmployees={allEmployees}
        onCreateMeetingWithCoworker={onCreateMeetingWithCoworker}
      />

      {/* Divider */}
      {managedEmployees && managedEmployees.length > 0 && (
        <>
          <div className="flex justify-center py-6">
            <div className="w-[90%] border-t border-slate-200" />
          </div>

          {/* Team Section */}
          <div className="flex-1 overflow-hidden px-6 pb-2">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-xs font-medium uppercase tracking-wide text-slate-500">
                {t('yourTeam')}
              </h3>
              <div className="flex items-center gap-2 text-primary">
                <span className="text-sm font-bold">
                  {managedEmployees.length}
                </span>
                <Users className="h-4 w-4" />
              </div>
            </div>
            <div className="h-full overflow-y-auto pb-6">
              <div className="space-y-3">
                {managedEmployees.map((employee) => {
                  const isSelected = displayEmployee?._id === employee._id;

                  return (
                    <MeetingCard
                      key={employee._id}
                      employeeName={`${employee.firstName} ${employee.lastName}`}
                      employeeImageUrl={employee.imageUrl}
                      lastMeetingDate={employee.lastMeetingDate || null}
                      isSelected={isSelected}
                      onSelect={() =>
                        onSelectEmployee({
                          ...employee,
                          meetingRole: 'manager',
                        })
                      }
                    />
                  );
                })}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Professional Meetings (as Facilitator) Section */}
      <ProfessionalMeetingsAsFacilitator
        professionalMeetings={professionalMeetingsAsManager || []}
        displayEmployee={displayEmployee}
        onSelectEmployee={onSelectEmployee}
        allEmployees={allEmployees}
      />
    </Card>
  );
};
